import { isNativeLoginCancellation } from './nativeLoginCancellation';
import { API_BASE_URL } from './apiConfig';
import { getCrmRouterBasename } from './routerBasename';

type Stage = 'login' | 'oauth_callback' | 'silent_renew' | 'uncaught' | 'unhandled_rejection' | 'api' | 'bootstrap';
type Code = 'http_error' | 'network_error' | 'invalid_state' | 'access_denied' | 'login_required' | 'timeout' | 'type_error' | 'syntax_error' | 'configuration_error' | 'invalid_response' | 'unknown';
type Endpoint = 'auth_session' | 'auth_login' | 'auth_native' | 'auth_other' | 'leads' | 'deals' | 'settings' | 'notifications' | 'other';
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
let transport: typeof fetch | undefined;
let sessionId: string;
let pageId: string;
let sessionExpires = 0;
let sequence = 0;
let windowStart = 0;
let sent = 0;
const seen = new Map<string, number>();

/** Only classify fixed known conditions; never send the message or arbitrary error properties. */
export function classifyCrmError(error: unknown): Code {
  try {
    if (error instanceof Error) {
      if (/^(No matching state found in storage|Invalid state)(\.|$)/i.test(error.message)) return 'invalid_state';
      if (error.message === 'Login expired' || /^(Timeout when waiting for|IFrame timed out|Network request timed out)/i.test(error.message) || error.name === 'TimeoutError') return 'timeout';
      if (error.name === 'TypeError') return /^(Failed to fetch|NetworkError when attempting to fetch resource\.|Load failed)$/.test(error.message) ? 'network_error' : 'type_error';
      if (error.name === 'SyntaxError') return 'syntax_error';
    }
    const code = (error as { error?: unknown })?.error;
    if (code === 'access_denied' || code === 'login_required') return code;
  } catch { /* Arbitrary rejection objects may have throwing getters. */ }
  return 'unknown';
}
function logicalPath() {
  const basename = getCrmRouterBasename();
  return (basename ? window.location.pathname.slice(basename.length) : window.location.pathname) || '/';
}
function safeRoute() {
  const path = logicalPath();
  if (path === '/') return 'home';
  if (/^\/(?:auth\/)?callback\/?$/.test(path)) return 'callback';
  for (const route of ['login', 'leads', 'deals', 'notifications', 'settings'] as const)
    if (path === `/${route}` || path.startsWith(`/${route}/`)) return route;
  return 'other';
}
function newUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // iOS 15.0–15.3 supports secure random bytes but not randomUUID.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function ids(resetSession = false) {
  const now = Date.now();
  pageId ??= newUuid();
  if (resetSession) { sessionId = ''; sessionExpires = 0; }
  if (!sessionId || sessionExpires <= now) {
    sessionId = '';
    try {
      const saved = resetSession ? null : JSON.parse(sessionStorage.getItem('crm.error-session') || 'null');
      if (uuid.test(saved?.id) && saved.expires > now && saved.expires <= now + 1800000 && Number.isFinite(saved.lastObservedAt) && saved.lastObservedAt <= now) {
        sessionId = saved.id;
        sessionExpires = saved.expires;
      }
    } catch { /* Storage is optional. */ }
    if (!sessionId) {
      sessionId = newUuid();
      sessionExpires = now + 1800000;
    }
  }
  try { sessionStorage.setItem('crm.error-session', JSON.stringify({ id: sessionId, expires: sessionExpires, lastObservedAt: now })); } catch { /* Optional. */ }
}
export function reportCrmError(stage: Stage, error?: unknown, details: { code?: Code; endpoint?: Endpoint; method?: string; status?: number; requestId?: string | null } = {}) {
  try {
    if (!transport || stage === 'login' && isNativeLoginCancellation(error)) return;
    const now = Date.now();
    const clockMovedBackwards = windowStart > now;
    if (now - windowStart >= 60000 || clockMovedBackwards) { windowStart = now; sent = 0; seen.clear(); }
    const code = details.code ?? classifyCrmError(error);
    const key = `${stage}:${code}:${details.endpoint}:${details.status}`;
    if (sent >= 20 || now - (seen.get(key) ?? 0) < 5000) return;
    ids(clockMovedBackwards);
    seen.set(key, now); sent++;
    const method = details.method?.toUpperCase();
    const build = import.meta.env.VITE_DIAGNOSTIC_BUILD;
    const body = JSON.stringify({ sessionId, pageId, sequence: ++sequence, timestamp: new Date(now).toISOString(), stage, code,
      route: safeRoute(), endpoint: details.endpoint,
      method: method ? (['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].includes(method) ? method : 'OTHER') : undefined,
      status: details.status, requestId: details.requestId && uuid.test(details.requestId) ? details.requestId : undefined,
      build: typeof build === 'string' && /^[a-f0-9]{7,40}$/.test(build) ? build : 'unknown', online: navigator.onLine !== false });
    // Captured transport bypasses instrumentation. No cookies, auth, response body, retries or persistent queue.
    void transport(`${API_BASE_URL}/crm-errors`, { method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* Telemetry must never interfere with the application. */ }
}
function isAbort(error: unknown) {
  try { return (error as { name?: unknown })?.name === 'AbortError'; } catch { return false; }
}
function endpointFor(path: string): Endpoint {
  if (path === '/api/auth/application-session') return 'auth_session';
  if (['/api/auth/sign-in', '/api/auth/login-options'].includes(path)) return 'auth_login';
  if (path.startsWith('/api/auth/native/')) return 'auth_native';
  if (path.startsWith('/api/auth/')) return 'auth_other';
  for (const family of ['leads', 'deals', 'settings', 'notifications'] as const)
    if (path === `/api/${family}` || path.startsWith(`/api/${family}/`)) return family;
  return 'other';
}
/** Install before App/SDK evaluation so pre-auth and direct fetch callers are covered. */
export function installCrmErrorReporting(): () => void {
  if (typeof window.fetch !== 'function') return () => {};
  const original = window.fetch;
  transport = original.bind(window);
  const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
  const wrapped: typeof fetch = async (input, init) => {
    let details: { endpoint: Endpoint; method: string } | undefined;
    try {
      const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, window.location.origin);
      if (url.origin === apiOrigin && url.pathname.startsWith('/api/') && !['/api/crm-errors', '/api/notification-diagnostics', '/api/viewer-errors'].includes(url.pathname.replace(/\/$/, ''))) {
        details = { endpoint: endpointFor(url.pathname), method: init?.method ?? (input instanceof Request ? input.method : 'GET') };
      }
    } catch { /* Invalid requests still retain native fetch behavior. */ }
    try {
      const response = await original.call(window, input, init);
      if (details && response.status >= 400) reportCrmError('api', undefined, { ...details, code: 'http_error', status: response.status, requestId: response.headers.get('X-Request-ID') });
      return response;
    } catch (error) {
      if (details && !isAbort(error)) reportCrmError('api', error, { ...details, code: 'network_error' });
      throw error;
    }
  };
  window.fetch = wrapped;
  // Full-document Auth0/better-auth form callbacks land here rather than in an SDK promise.
  try {
    const params = new URLSearchParams(window.location.search);
    if (logicalPath() === '/login' && params.has('error')) {
      const reason = params.get('error');
      reportCrmError('oauth_callback', { error: reason === 'sso_access_denied' ? 'access_denied' : reason });
    }
  } catch { /* Optional callback metadata. */ }
  const onError = (event: ErrorEvent) => reportCrmError('uncaught', event.error);
  const onRejection = (event: PromiseRejectionEvent) => reportCrmError('unhandled_rejection', event.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    if (window.fetch === wrapped) window.fetch = original;
    transport = undefined;
  };
}
